import { Component, EventEmitter, Input, Output } from '@angular/core';

import { OnInit } from '@angular/core';
import {
  AbstractControl,
  UntypedFormBuilder,
  UntypedFormGroup,
  ValidationErrors,
} from '@angular/forms';

import { TranslocoService } from '@jsverse/transloco';
import { capitalizeAll } from 'humanize-plus';
import { EnumErrorStates, ErrorObject } from '@inmoment/the-kitchen';

import {
  ActionPlanInfo,
  ActionPlanIpdService,
} from 'app/modules/widget/services/action-plan-ipd.service';
import { Dashboard } from 'app/providers/bridge-api/dashboard.service';
import { GroupService } from 'app/providers/semantria-api/group.service';
import { ProjectService } from 'app/providers/storage-api/project.service';
import {
  injectComponentTelemetryService,
  EnableComponentTelemetry,
  EnableClassTelemetry,
} from 'app/services/otel.service';

import { Utils } from 'app/utils/utils';
import { WidgetDataSource } from '../../../../models';
import {
  ActionItem,
  ActionPlan,
  ActionPlanService,
} from '../../../../providers/storage-api/actionplan.service';
import { AlertService } from '../../../../services/alert.service';

@EnableClassTelemetry()
@Component({
  selector: 'app-create-action-item-dialog',
  templateUrl: './create-action-item-dialog.component.html',
  styleUrls: ['./create-action-item-dialog.component.scss'],
  standalone: false,
})
export class CreateActionItemDialogComponent implements OnInit {
  @EnableComponentTelemetry() otelService = injectComponentTelemetryService();
  @Input() actionPlanInfo: ActionPlanInfo;
  @Input() value?: string;
  @Input() type?: string;
  @Input() actionItem: ActionItem;
  @Input() widgetDataSource?: WidgetDataSource;
  @Input() dashboard?: Dashboard;
  @Input() fromDashboard = true;

  @Output() create = new EventEmitter<ActionItem>();
  @Output() update = new EventEmitter<ActionItem>();
  @Output() createActionPlan = new EventEmitter<ActionPlan>();
  @Output() modalClosed = new EventEmitter<boolean>();

  public readonly capitalizeAll = capitalizeAll;

  public currentStep = 1;
  public isSaving = false;
  public form: UntypedFormGroup;
  public actionItemMinDueDate: Date;
  public actionItemMaxDueDate: Date = undefined;
  public dueDateStatus: ErrorObject = { state: EnumErrorStates.SUCCESS };
  public nameStatus: ErrorObject = { state: EnumErrorStates.SUCCESS };
  public hasActionPlan = true;

  public titleLabel = 'Create Action Item';
  public actionLabel = 'created';
  public buttonLabel = 'Create Action Item';

  private scoringStrategy: 'AVERAGE' | 'BOX' | 'NPS';

  constructor(
    private alertService: AlertService,
    private actionPlanService: ActionPlanService,
    private groupService: GroupService,
    private projectService: ProjectService,
    private actionPlanIpdService: ActionPlanIpdService,
    private translocoService: TranslocoService,
    formBuilder: UntypedFormBuilder
  ) {
    this.form = formBuilder.group({
      actionItemNote: [''],
      actionItemAssignee: [''],
      actionItemDueDate: [
        '',
        this.validatorFactory(
          this.dueDateStatus,
          translocoService.translate('create-action-item-dialog-component.due-date')
        ),
      ],
      actionItemName: [
        '',
        this.validatorFactory(
          this.nameStatus,
          translocoService.translate('create-action-item-dialog-component.item-name-field')
        ),
      ],
    });

    this.actionItemMinDueDate = new Date();
    this.actionItemMinDueDate.setDate(this.actionItemMinDueDate.getDate() - 1);

    const sub = this.actionPlanIpdService.onTwtraScoringStrategyChanged.subscribe(
      (s) => (this.scoringStrategy = s)
    );
    sub.unsubscribe();
  }

  ngOnInit(): void {
    this.hasActionPlan = this.actionPlanInfo.actionPlan !== undefined;

    if (this.hasActionPlan) {
      this.form.patchValue({
        actionItemDueDate: new Date(this.actionPlanInfo.actionPlan.due_date),
      });

      this.actionItemMaxDueDate = new Date(this.actionPlanInfo.actionPlan.due_date);
    }

    if (this.actionItem) {
      this.titleLabel = this.translocoService.translate(
        'create-action-item-dialog-component.save-title-label'
      );
      this.actionLabel = this.translocoService.translate(
        'create-action-item-dialog-component.save-action-label'
      );
      this.buttonLabel = this.translocoService.translate(
        'create-action-item-dialog-component.save-button-label'
      );

      this.form.patchValue({
        actionItemNote: this.actionItem.notes,
        actionItemAssignee: this.actionItem.assignee_user_name,
        actionItemDueDate: new Date(this.actionItem.due_date),
        actionItemName: this.actionItem.name,
      });
    } else {
      this.titleLabel = this.translocoService.translate(
        'create-action-item-dialog-component.title-label'
      );
      this.actionLabel = this.translocoService.translate(
        'create-action-item-dialog-component.action-label'
      );
      this.buttonLabel = this.translocoService.translate(
        'create-action-item-dialog-component.button-label'
      );
    }
  }

  private validatorFactory(status: ErrorObject, fieldName: string) {
    return (control: AbstractControl): ValidationErrors => {
      if (!control.value) {
        if (control.touched || !control.pristine) {
          status.state = 'error';
          status.text = this.translocoService.translate(
            'create-action-item-dialog-component.is-required',
            { fieldName }
          );
        }

        return { required: true };
      }

      status.state = EnumErrorStates.SUCCESS;
      status.text = null;
      return null;
    };
  }

  public onCancel(): void {
    this.modalClosed.emit(false);
  }

  public onBack(): void {
    this.modalClosed.emit(false);
  }

  public onAddTask(): void {
    this.modalClosed.emit(true);
  }

  public async onCreateActionItem() {
    this.isSaving = true;

    this.form.disable();

    let actionPlanId: string;
    if (!this.hasActionPlan) {
      try {
        const project = await this.projectService.getProjectById(this.dashboard.project_id, false);

        const actionPlanToCreate = ActionPlanService.createActionPlanObject({
          calculationId: this.widgetDataSource.id, // widgetDataSource has to be WidgetKeyDriverAnalysisDataSource
          collectionName: this.widgetDataSource.collectionName,
          collectionDisplayName: this.widgetDataSource.displayName,
          driverName: this.actionPlanInfo.driverName,
          driverColumn: this.actionPlanInfo.driverColumn,
          breakdown: this.actionPlanInfo.breakdownName,
          breakdownColumn: this.actionPlanInfo.breakdownColumn,
          notes: '',
          dueDate: Utils.getDateIsoString(this.form.controls.actionItemDueDate.value),
          originalMetric: this.actionPlanInfo.value,
          targetMetric: this.actionPlanInfo.simulatedValue,
          projectId: project.id,
          projectName: project.name,
          dashboardId: this.dashboard.id,
          scoringStrategy: this.scoringStrategy,
        });

        const groupId = this.groupService.getSelectedGroupId();
        const createdActionPlan = await this.actionPlanService.createActionPlan(
          groupId,
          actionPlanToCreate
        );

        this.createActionPlan.emit(createdActionPlan);

        actionPlanId = createdActionPlan.plan_id;
      } catch (error) {
        this.alertService.showErrorToast(
          this.translocoService.translate('create-action-item-dialog-component.failed-to-create'),
          error
        );

        this.isSaving = false;
        return;
      }
    } else {
      actionPlanId = this.actionPlanInfo.actionPlan.plan_id;
    }

    if (this.actionItem) {
      this.actionItem.name = this.form.controls.actionItemName.value;
      this.actionItem.due_date = Utils.getDateIsoString(this.form.controls.actionItemDueDate.value);
      this.actionItem.assignee_user_name = this.form.controls.actionItemAssignee.value;
      this.actionItem.notes = this.form.controls.actionItemNote.value;

      try {
        const updatedActionItem = await this.actionPlanService.updateActionItem(
          actionPlanId,
          this.actionItem
        );

        this.update.emit(updatedActionItem);

        this.currentStep = 2;
      } catch (error) {
        this.alertService.showErrorToast(
          this.translocoService.translate('create-action-item-dialog-component.failed-to-update'),
          error
        );
      }
    } else {
      try {
        const actionItemToCreate = ActionPlanService.createActionItemObject({
          name: this.form.controls.actionItemName.value,
          dueDate: Utils.getDateIsoString(this.form.controls.actionItemDueDate.value),
          assignee: this.form.controls.actionItemAssignee.value,
          notes: this.form.controls.actionItemNote.value,
          itemType: this.type,
          itemValue: this.value,
        });

        const createdActionItem = await this.actionPlanService.createActionItem(
          actionPlanId,
          actionItemToCreate
        );

        this.create.emit(createdActionItem);

        this.currentStep = 2;
      } catch (error) {
        this.alertService.showErrorToast(
          this.translocoService.translate(
            'create-action-item-dialog-component.failed-to-create-item'
          ),
          error
        );
      }
    }

    this.form.enable();

    this.isSaving = false;
  }

  get breakdown(): string {
    return this.actionPlanInfo.actionPlan?.metadata.breakdown ?? this.actionPlanInfo.breakdownName;
  }

  get driverName(): string {
    return this.actionPlanInfo.actionPlan?.metric_name ?? this.actionPlanInfo.driverName;
  }

  get tag(): string {
    return this.actionItem?.item_value ?? this.value;
  }
}
